/**
 * In-App message design defaults.
 *
 * Each entry ships a complete InAppState ready to drop into the In-App
 * builder. Operators pick one as a starting point and edit freely.
 */

import {
  Alignment,
  BackgroundType,
  DismissPosition,
  DismissType,
  GeneralClickAction,
  InAppPosition,
  InAppState,
  MediaClickAction,
  MediaPosition,
  MediaType,
  PrimaryButtonPosition,
  SizeUnit,
  AdditionalClickOptions,
  AdditionalClicks,
} from "pages/InAppBuilder/types";

export interface InAppDefault {
  key: string;
  category: string;
  name: string;
  description: string;
  state: InAppState;
}

const additionalClicks = (): AdditionalClicks => ({
  [AdditionalClickOptions.OPENURL]: {
    action: AdditionalClickOptions.OPENURL,
    hidden: true,
    object: { openNewTab: true, url: "" },
  },
  [AdditionalClickOptions.NOACTION]: {
    action: AdditionalClickOptions.NOACTION,
    hidden: true,
    object: undefined,
  },
});

const baseState = (overrides: Partial<InAppState> = {}): InAppState => ({
  position: InAppPosition.CENTER,
  xOffset: { value: 0, unit: SizeUnit.PIXEL },
  yOffset: { value: 0, unit: SizeUnit.PIXEL },
  width: { value: 400, unit: SizeUnit.PIXEL },
  borderRadius: { value: 16, unit: SizeUnit.PIXEL },
  background: {
    selected: BackgroundType.SOLID,
    [BackgroundType.SOLID]: {
      type: BackgroundType.SOLID,
      color: "#FFFFFF",
      opacity: 1,
    },
    [BackgroundType.GRADIENT]: {
      type: BackgroundType.GRADIENT,
      color1: "#6366F1",
      color2: "#A855F7",
      opacity: 1,
    },
    [BackgroundType.IMAGE]: {
      type: BackgroundType.IMAGE,
      key: null,
      imageSrc: "",
    },
  },
  title: {
    hidden: false,
    alignment: Alignment.CENTER,
    content: "**Headline goes here**",
    fontSize: 20,
    textColor: "#111827",
    linkColor: "#6366F1",
  },
  body: {
    hidden: false,
    alignment: Alignment.CENTER,
    content:
      "Drop a short, useful message in here. Two sentences max keeps it readable.",
    fontSize: 14,
    textColor: "#374151",
    linkColor: "#6366F1",
  },
  media: {
    hidden: true,
    type: MediaType.IMAGE,
    imageSrc: "",
    key: null,
    altText: "",
    actionOnClick: MediaClickAction.NONE,
    height: { value: 60, unit: SizeUnit.PERCENTAGE },
    position: MediaPosition.TOP,
    videoUrl: null,
    additionalClick: additionalClicks(),
  },
  primaryButton: {
    hidden: false,
    content: "Continue",
    fillColor: "#0F172A",
    borderColor: "#0F172A",
    textColor: "#FFFFFF",
    borderRadius: { value: 8, unit: SizeUnit.PIXEL },
    position: PrimaryButtonPosition.BOTTOM_CENTER,
    clickAction: GeneralClickAction.NONE,
    additionalClick: additionalClicks(),
  },
  dismiss: {
    hidden: false,
    content: "close",
    type: DismissType.CROSS,
    textSize: 14,
    color: "#9CA3AF",
    position: DismissPosition.INSIDE_RIGHT,
    timedDismiss: {
      enabled: false,
      duration: 3,
      displayTimer: false,
      timerColor: "#1CC88A",
    },
  },
  shroud: {
    hidden: false,
    color: "#000000",
    opacity: 0.5,
    blur: 2,
  },
  emailTemplate: null,
  emailHtml: null,
  ...overrides,
});

// ---------- 1. Classic Centered Modal ----------
const classicCenteredModal: InAppDefault = {
  key: "inapp.classic-centered",
  category: "Modal",
  name: "Classic Centered Modal",
  description:
    "White card centered with image on top, title, body, and a single CTA. The friendly default.",
  state: baseState({
    width: { value: 420, unit: SizeUnit.PIXEL },
    borderRadius: { value: 16, unit: SizeUnit.PIXEL },
    media: {
      hidden: false,
      type: MediaType.IMAGE,
      imageSrc: "",
      key: null,
      altText: "",
      actionOnClick: MediaClickAction.NONE,
      height: { value: 50, unit: SizeUnit.PERCENTAGE },
      position: MediaPosition.TOP,
      videoUrl: null,
      additionalClick: additionalClicks(),
    },
  }),
};

// ---------- 2. Bottom-Right Toast ----------
const bottomRightToast: InAppDefault = {
  key: "inapp.bottom-right-toast",
  category: "Toast",
  name: "Bottom-Right Toast",
  description:
    "Small notification anchored bottom-right. Body only, no shroud. Quiet by design.",
  state: baseState({
    position: InAppPosition.BOTTOM_RIGHT,
    xOffset: { value: 24, unit: SizeUnit.PIXEL },
    yOffset: { value: 24, unit: SizeUnit.PIXEL },
    width: { value: 320, unit: SizeUnit.PIXEL },
    borderRadius: { value: 12, unit: SizeUnit.PIXEL },
    background: {
      selected: BackgroundType.SOLID,
      [BackgroundType.SOLID]: {
        type: BackgroundType.SOLID,
        color: "#0F172A",
        opacity: 1,
      },
      [BackgroundType.GRADIENT]: {
        type: BackgroundType.GRADIENT,
        color1: "#6366F1",
        color2: "#A855F7",
        opacity: 1,
      },
      [BackgroundType.IMAGE]: {
        type: BackgroundType.IMAGE,
        key: null,
        imageSrc: "",
      },
    },
    title: {
      hidden: true,
      alignment: Alignment.LEFT,
      content: "",
      fontSize: 14,
      textColor: "#FFFFFF",
      linkColor: "#6366F1",
    },
    body: {
      hidden: false,
      alignment: Alignment.LEFT,
      content:
        "**A short note for you.** One line of context here and they can dismiss.",
      fontSize: 13,
      textColor: "#F9FAFB",
      linkColor: "#A5B4FC",
    },
    media: {
      hidden: true,
      type: MediaType.IMAGE,
      imageSrc: "",
      key: null,
      altText: "",
      actionOnClick: MediaClickAction.NONE,
      height: { value: 40, unit: SizeUnit.PERCENTAGE },
      position: MediaPosition.TOP,
      videoUrl: null,
      additionalClick: additionalClicks(),
    },
    primaryButton: {
      hidden: true,
      content: "",
      fillColor: "#1A86FF",
      borderColor: "#1A86FF",
      textColor: "#FFFFFF",
      borderRadius: { value: 6, unit: SizeUnit.PIXEL },
      position: PrimaryButtonPosition.BOTTOM_CENTER,
      clickAction: GeneralClickAction.NONE,
      additionalClick: additionalClicks(),
    },
    shroud: {
      hidden: true,
      color: "#000000",
      opacity: 0,
      blur: 0,
    },
    dismiss: {
      hidden: false,
      content: "close",
      type: DismissType.CROSS,
      textSize: 12,
      color: "#9CA3AF",
      position: DismissPosition.INSIDE_RIGHT,
      timedDismiss: {
        enabled: false,
        duration: 3,
        displayTimer: false,
        timerColor: "#1CC88A",
      },
    },
  }),
};

// ---------- 3. Top Banner ----------
const topBanner: InAppDefault = {
  key: "inapp.top-banner",
  category: "Banner",
  name: "Top Banner",
  description:
    "Slim full-width banner anchored to the top. For announcements and persistent notices.",
  state: baseState({
    position: InAppPosition.TOP_CENTER,
    yOffset: { value: 0, unit: SizeUnit.PIXEL },
    width: { value: 720, unit: SizeUnit.PIXEL },
    borderRadius: { value: 0, unit: SizeUnit.PIXEL },
    background: {
      selected: BackgroundType.SOLID,
      [BackgroundType.SOLID]: {
        type: BackgroundType.SOLID,
        color: "#6366F1",
        opacity: 1,
      },
      [BackgroundType.GRADIENT]: {
        type: BackgroundType.GRADIENT,
        color1: "#6366F1",
        color2: "#A855F7",
        opacity: 1,
      },
      [BackgroundType.IMAGE]: {
        type: BackgroundType.IMAGE,
        key: null,
        imageSrc: "",
      },
    },
    title: {
      hidden: true,
      alignment: Alignment.LEFT,
      content: "",
      fontSize: 14,
      textColor: "#FFFFFF",
      linkColor: "#FFFFFF",
    },
    body: {
      hidden: false,
      alignment: Alignment.LEFT,
      content:
        "**New:** Replace this line with whatever you want everyone to see right now.",
      fontSize: 14,
      textColor: "#FFFFFF",
      linkColor: "#FFFFFF",
    },
    media: {
      hidden: true,
      type: MediaType.IMAGE,
      imageSrc: "",
      key: null,
      altText: "",
      actionOnClick: MediaClickAction.NONE,
      height: { value: 40, unit: SizeUnit.PERCENTAGE },
      position: MediaPosition.LEFT,
      videoUrl: null,
      additionalClick: additionalClicks(),
    },
    primaryButton: {
      hidden: false,
      content: "Learn more",
      fillColor: "#FFFFFF",
      borderColor: "#FFFFFF",
      textColor: "#4338CA",
      borderRadius: { value: 6, unit: SizeUnit.PIXEL },
      position: PrimaryButtonPosition.CENTER_RIGHT,
      clickAction: GeneralClickAction.NONE,
      additionalClick: additionalClicks(),
    },
    shroud: {
      hidden: true,
      color: "#000000",
      opacity: 0,
      blur: 0,
    },
  }),
};

// ---------- 4. Gradient Hero ----------
const gradientHero: InAppDefault = {
  key: "inapp.gradient-hero",
  category: "Modal",
  name: "Gradient Hero",
  description:
    "Centered modal with a bold indigo→purple gradient background. For launches and announcements.",
  state: baseState({
    width: { value: 460, unit: SizeUnit.PIXEL },
    borderRadius: { value: 20, unit: SizeUnit.PIXEL },
    background: {
      selected: BackgroundType.GRADIENT,
      [BackgroundType.SOLID]: {
        type: BackgroundType.SOLID,
        color: "#FFFFFF",
        opacity: 1,
      },
      [BackgroundType.GRADIENT]: {
        type: BackgroundType.GRADIENT,
        color1: "#6366F1",
        color2: "#A855F7",
        opacity: 1,
      },
      [BackgroundType.IMAGE]: {
        type: BackgroundType.IMAGE,
        key: null,
        imageSrc: "",
      },
    },
    title: {
      hidden: false,
      alignment: Alignment.CENTER,
      content: "**Something new is here**",
      fontSize: 22,
      textColor: "#FFFFFF",
      linkColor: "#FDE68A",
    },
    body: {
      hidden: false,
      alignment: Alignment.CENTER,
      content:
        "A short, exciting sentence about what's new and why anyone should care.",
      fontSize: 14,
      textColor: "#EDE9FE",
      linkColor: "#FDE68A",
    },
    media: {
      hidden: true,
      type: MediaType.IMAGE,
      imageSrc: "",
      key: null,
      altText: "",
      actionOnClick: MediaClickAction.NONE,
      height: { value: 50, unit: SizeUnit.PERCENTAGE },
      position: MediaPosition.TOP,
      videoUrl: null,
      additionalClick: additionalClicks(),
    },
    primaryButton: {
      hidden: false,
      content: "Check it out",
      fillColor: "#FFFFFF",
      borderColor: "#FFFFFF",
      textColor: "#4338CA",
      borderRadius: { value: 8, unit: SizeUnit.PIXEL },
      position: PrimaryButtonPosition.BOTTOM_CENTER,
      clickAction: GeneralClickAction.NONE,
      additionalClick: additionalClicks(),
    },
    shroud: {
      hidden: false,
      color: "#000000",
      opacity: 0.6,
      blur: 4,
    },
  }),
};

// ---------- 5. Dark Premium ----------
const darkPremium: InAppDefault = {
  key: "inapp.dark-premium",
  category: "Modal",
  name: "Dark Premium",
  description:
    "Dark slate card with amber accent button. Image at the top. Looks premium.",
  state: baseState({
    width: { value: 440, unit: SizeUnit.PIXEL },
    borderRadius: { value: 14, unit: SizeUnit.PIXEL },
    background: {
      selected: BackgroundType.SOLID,
      [BackgroundType.SOLID]: {
        type: BackgroundType.SOLID,
        color: "#0F172A",
        opacity: 1,
      },
      [BackgroundType.GRADIENT]: {
        type: BackgroundType.GRADIENT,
        color1: "#6366F1",
        color2: "#A855F7",
        opacity: 1,
      },
      [BackgroundType.IMAGE]: {
        type: BackgroundType.IMAGE,
        key: null,
        imageSrc: "",
      },
    },
    title: {
      hidden: false,
      alignment: Alignment.LEFT,
      content: "**A clean line of premium intent**",
      fontSize: 20,
      textColor: "#F9FAFB",
      linkColor: "#F59E0B",
    },
    body: {
      hidden: false,
      alignment: Alignment.LEFT,
      content:
        "Two lines of supporting copy in a soft grey. Restrained and confident.",
      fontSize: 14,
      textColor: "#9CA3AF",
      linkColor: "#F59E0B",
    },
    media: {
      hidden: false,
      type: MediaType.IMAGE,
      imageSrc: "",
      key: null,
      altText: "",
      actionOnClick: MediaClickAction.NONE,
      height: { value: 45, unit: SizeUnit.PERCENTAGE },
      position: MediaPosition.TOP,
      videoUrl: null,
      additionalClick: additionalClicks(),
    },
    primaryButton: {
      hidden: false,
      content: "Continue",
      fillColor: "#F59E0B",
      borderColor: "#F59E0B",
      textColor: "#0F172A",
      borderRadius: { value: 6, unit: SizeUnit.PIXEL },
      position: PrimaryButtonPosition.BOTTOM_LEFT,
      clickAction: GeneralClickAction.NONE,
      additionalClick: additionalClicks(),
    },
    shroud: {
      hidden: false,
      color: "#000000",
      opacity: 0.7,
      blur: 4,
    },
    dismiss: {
      hidden: false,
      content: "close",
      type: DismissType.CROSS,
      textSize: 14,
      color: "#6B7280",
      position: DismissPosition.INSIDE_RIGHT,
      timedDismiss: {
        enabled: false,
        duration: 3,
        displayTimer: false,
        timerColor: "#1CC88A",
      },
    },
  }),
};

// ---------- 6. Image-Led Showcase ----------
const imageLed: InAppDefault = {
  key: "inapp.image-led",
  category: "Modal",
  name: "Image-Led Showcase",
  description:
    "Large image takes most of the card. Short title and CTA underneath, visual-first.",
  state: baseState({
    width: { value: 480, unit: SizeUnit.PIXEL },
    borderRadius: { value: 18, unit: SizeUnit.PIXEL },
    title: {
      hidden: false,
      alignment: Alignment.CENTER,
      content: "**A short, image-led headline**",
      fontSize: 18,
      textColor: "#0F172A",
      linkColor: "#6366F1",
    },
    body: {
      hidden: false,
      alignment: Alignment.CENTER,
      content: "One supporting line, no more.",
      fontSize: 13,
      textColor: "#6B7280",
      linkColor: "#6366F1",
    },
    media: {
      hidden: false,
      type: MediaType.IMAGE,
      imageSrc: "",
      key: null,
      altText: "",
      actionOnClick: MediaClickAction.NONE,
      height: { value: 65, unit: SizeUnit.PERCENTAGE },
      position: MediaPosition.TOP,
      videoUrl: null,
      additionalClick: additionalClicks(),
    },
    primaryButton: {
      hidden: false,
      content: "View",
      fillColor: "#6366F1",
      borderColor: "#6366F1",
      textColor: "#FFFFFF",
      borderRadius: { value: 999, unit: SizeUnit.PIXEL },
      position: PrimaryButtonPosition.BOTTOM_CENTER,
      clickAction: GeneralClickAction.NONE,
      additionalClick: additionalClicks(),
    },
  }),
};

export const IN_APP_DEFAULTS: InAppDefault[] = [
  classicCenteredModal,
  bottomRightToast,
  topBanner,
  gradientHero,
  darkPremium,
  imageLed,
];
